import React, { useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import TextInput from './ui/text-input';
import { apiService } from '../services/apiService';
import { useAuthStore } from '../store/authStore';
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from '../hooks/useBrandingColors';
import ComplaintConfirmationModal from './ComplaintConfirmationModal';

interface ComplaintModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MAX_TEXT_LENGTH = 5000;
const MAX_EMAIL_LENGTH = 320;

export const ComplaintModal: React.FC<ComplaintModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuthStore();
  const [email, setEmail] = useState<string>('');
  const [text, setText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ email?: string; text?: string }>({});
  const colors = useBrandingColors();
  const [isPrimaryHover, setIsPrimaryHover] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  React.useEffect(() => {
    if (isOpen) {
      setErrors({});
      setShowConfirmation(false);
      // Автоподстановка email, если известен
      setEmail(user?.email || '');
      setText('');
    }
  }, [isOpen, user?.email]);

  const remaining = useMemo(() => Math.max(0, MAX_TEXT_LENGTH - (text?.length || 0)), [text]);

  const validate = (): boolean => {
    const next: { email?: string; text?: string } = {};
    const emailTrimmed = (email || '').trim();
    if (!emailTrimmed) next.email = 'Укажите email';
    else if (emailTrimmed.length > MAX_EMAIL_LENGTH) next.email = 'Слишком длинный email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) next.email = 'Некорректный email';

    const textTrimmed = (text || '').trim();
    if (!textTrimmed) next.text = 'Опишите проблему';
    else if (textTrimmed.length > MAX_TEXT_LENGTH) next.text = 'Слишком длинный текст';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      setErrors({});
      await apiService.createComplaint({ senderEmail: email.trim(), text: text.trim() });
      // Сначала закрываем основное модальное окно
      onClose();
      // Затем показываем модальное окно подтверждения
      setTimeout(() => setShowConfirmation(true), 100);
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || 'Не удалось отправить жалобу';
      const fieldErrors: string[] | undefined = err?.response?.data?.errors;
      if (status === 400 && Array.isArray(fieldErrors)) {
        const next: { email?: string; text?: string } = {};
        for (const m of fieldErrors) {
          if (/email/i.test(m)) next.email = 'Проверьте email';
          if (/text|описани/i.test(m)) next.text = 'Проверьте текст жалобы';
        }
        setErrors(next);
        toast.error('Проверьте данные');
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="complaint-title">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 id="complaint-title" className="text-lg font-semibold text-gray-900">Отправить жалобу</h3>
              <button onClick={handleClose} disabled={loading} className="text-gray-400 hover:text-gray-600 disabled:opacity-50" aria-label="Закрыть">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ваш email</label>
                  <TextInput value={email} onChange={(v) => { setEmail(v); if (errors.email) setErrors(prev => ({ ...prev, email: undefined })); }} placeholder="email@example.com" name="senderEmail" autoComplete="email" />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Текст жалобы</label>
                  <textarea
                    value={text}
                    onChange={(e) => { setText(e.target.value); if (errors.text) setErrors(prev => ({ ...prev, text: undefined })); }}
                    placeholder="Опишите проблему"
                    rows={6}
                    className="w-full px-4 py-3 border rounded-2xl text-[16px] text-gray-500 resize-y min-h-[160px] focus:outline-none"
                    style={{ borderColor: 'var(--border-light)' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = colors.primary; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-light)'; }}
                    aria-describedby={errors.text ? 'complaint-text-error' : undefined}
                  />
                  <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                    <span>{remaining} / {MAX_TEXT_LENGTH}</span>
                    {errors.text && <span id="complaint-text-error" className="text-red-600">{errors.text}</span>}
                  </div>
                </div>
              </div>

              <div className="text-xs text-gray-500 mt-3">Отправляя жалобу, вы соглашаетесь с обработкой данных. Не указывайте конфиденциальную информацию.</div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="px-[16px] py-[14px] h-[52px] rounded-3xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200 text-gray-500 hover:bg-gray-50 font-['Inter:Medium',_sans-serif] font-medium text-[16px] text-center tracking-[-0.176px]"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={loading || !email.trim() || !text.trim()}
                  className="px-[16px] py-[14px] h-[52px] rounded-3xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white font-['Inter:Medium',_sans-serif] font-medium text-[16px] text-center tracking-[-0.176px]"
                  style={isPrimaryHover ? getButtonHoverStyles(colors, 'primary') : getButtonInlineStyles(colors, 'primary')}
                  onMouseEnter={() => setIsPrimaryHover(true)}
                  onMouseLeave={() => setIsPrimaryHover(false)}
                >
                  {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                  {loading ? 'Отправка…' : 'Отправить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <ComplaintConfirmationModal 
        isOpen={showConfirmation} 
        onClose={handleConfirmationClose} 
      />
    </>
  );
};

export default ComplaintModal;


